import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Video, Phone, PhoneIncoming, PhoneOff, Clock,
  ArrowLeft, Users, Loader2, Copy, Link2, Trash2,
  Calendar, Circle, Play, UserPlus, Mail, Send, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCall } from "@/contexts/CallContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDuration(seconds) {
  if (!seconds) return "--";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDate(isoStr) {
  if (!isoStr) return "--";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function getCallLabel(call, currentUserName) {
  const invitees = call.invitee_names || [];
  const creator = call.created_by || "Unknown";
  if (invitees.length > 0) {
    if (creator === currentUserName || call.created_by_id === currentUserName) {
      return invitees.join(", ");
    }
    return creator;
  }
  const participants = call.participant_names || [];
  const others = participants.filter(n => n !== currentUserName);
  if (others.length > 0) return others.join(", ");
  return creator;
}

// ─── Incoming Call Card ───
function IncomingCallCard({ req, onAccept, onDecline }) {
  return (
    <div
      className="bg-gradient-to-r from-green-500/15 to-emerald-500/10 border border-green-500/40 rounded-2xl p-4 flex items-center justify-between gap-3"
      data-testid={`pending-request-${req.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 animate-pulse">
          <PhoneIncoming className="w-5 h-5 text-green-400" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold text-sm">{req.caller_name}</p>
          <p className="text-green-300/70 text-xs">{req.message || "Incoming call..."}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={onAccept}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-11 h-11 p-0"
          data-testid={`accept-call-${req.id}`}
        >
          <Phone className="w-5 h-5" />
        </Button>
        <Button
          size="sm"
          onClick={onDecline}
          className="bg-white/10 hover:bg-red-500/20 text-red-400 rounded-full w-11 h-11 p-0"
          data-testid={`decline-call-${req.id}`}
        >
          <PhoneOff className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Person Picker (used for Call / Schedule) ───
function PersonPicker({ people, selected, onToggle, onClose, onAction, actionLabel, actionDisabled, loading: actionLoading }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="person-picker">
      <h3 className="text-white font-medium text-sm">Who do you want to call?</h3>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {people.map(p => (
          <button
            key={p.id}
            onClick={() => onToggle(p.id)}
            className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all ${
              selected.includes(p.id)
                ? "bg-[#00D4FF]/15 border border-[#00D4FF]/40"
                : "bg-white/5 border border-transparent hover:bg-white/10"
            }`}
            data-testid={`person-${p.id}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              selected.includes(p.id) ? "bg-[#00D4FF]/30 text-[#00D4FF]" : "bg-white/10 text-white/50"
            }`}>
              {(p.name || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{p.name || p.email}</p>
              {p.role && <p className="text-white/30 text-[10px]">{p.role}</p>}
            </div>
            {selected.includes(p.id) && (
              <div className="w-5 h-5 rounded-full bg-[#00D4FF] flex items-center justify-center flex-shrink-0">
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="2" strokeLinecap="round"/></svg>
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          className="flex-1 bg-[#00D4FF] hover:bg-[#00B8E0] text-black font-semibold text-sm h-11"
          onClick={onAction}
          disabled={actionDisabled || actionLoading}
          data-testid="person-picker-action-btn"
        >
          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
          {actionLabel}
        </Button>
        <Button
          variant="ghost"
          className="text-white/40 hover:text-white hover:bg-white/10 text-sm px-4 h-11"
          onClick={onClose}
          data-testid="person-picker-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ─── Schedule Form ───
function ScheduleForm({ people, selected, onToggle, scheduledDate, setScheduledDate, scheduledTime, setScheduledTime, onSchedule, onClose, loading: schedLoading }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="schedule-form">
      <h3 className="text-white font-medium text-sm">Schedule a call</h3>
      <div className="flex gap-2">
        <input
          type="date"
          value={scheduledDate}
          onChange={e => setScheduledDate(e.target.value)}
          className="flex-1 bg-white/10 border border-white/20 text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]/50"
          data-testid="schedule-date"
        />
        <input
          type="time"
          value={scheduledTime}
          onChange={e => setScheduledTime(e.target.value)}
          className="flex-1 bg-white/10 border border-white/20 text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]/50"
          data-testid="schedule-time"
        />
      </div>
      <p className="text-white/30 text-xs">Optionally invite someone:</p>
      <div className="space-y-1.5 max-h-32 overflow-y-auto">
        {people.map(p => (
          <button
            key={p.id}
            onClick={() => onToggle(p.id)}
            className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all text-sm ${
              selected.includes(p.id) ? "bg-[#00D4FF]/15 text-[#00D4FF]" : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
            data-testid={`schedule-person-${p.id}`}
          >
            <span className="truncate">{p.name || p.email}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          className="flex-1 bg-[#00D4FF] hover:bg-[#00B8E0] text-black font-semibold text-sm h-11"
          onClick={onSchedule}
          disabled={(!scheduledDate && !scheduledTime) || schedLoading}
          data-testid="schedule-confirm-btn"
        >
          {schedLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
          Schedule
        </Button>
        <Button variant="ghost" className="text-white/40 hover:text-white text-sm px-4 h-11" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── Active Call Row ───
function ActiveCallCard({ call, userName, onJoin, onCopyLink, onDelete }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between" data-testid={`active-call-${call.room_name}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-medium text-sm truncate">{getCallLabel(call, userName)}</p>
          <p className="text-white/30 text-xs">{formatDate(call.created_at)}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Button
          size="sm"
          className="bg-green-500 hover:bg-green-600 text-white text-xs px-4 h-9 rounded-xl font-semibold"
          onClick={onJoin}
          data-testid={`join-call-${call.room_name}`}
        >
          Join
        </Button>
        <button onClick={onCopyLink} className="text-white/25 hover:text-[#00D4FF] p-2 rounded-lg hover:bg-white/10" title="Copy link">
          <Link2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="text-white/15 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10" title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function VideoCallsPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const isAdmin = user.role === "admin";
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [calls, setCalls] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [creating, setCreating] = useState(false);

  // Flow states: null | "call" | "link" | "schedule"
  const [activeFlow, setActiveFlow] = useState(null);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  // View toggle
  const [showHistory, setShowHistory] = useState(false);
  // Link flow state
  const [linkUrl, setLinkUrl] = useState(null);
  const [linkRoomName, setLinkRoomName] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const togglePerson = (id) => {
    setSelectedPeople(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [historyRes, pendingRes, recordingsRes] = await Promise.all([
        axios.get(`${API}/video-calls/history`, authHeader),
        axios.get(`${API}/video-calls/call-requests/pending`, authHeader),
        axios.get(`${API}/video-calls/recordings`, authHeader),
      ]);
      setCalls(historyRes.data.calls || []);
      setPendingRequests(pendingRes.data.requests || []);
      setRecordings(recordingsRes.data.recordings || []);
    } catch (err) {
      console.error("Failed to fetch video call data:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      Promise.all([
        axios.get(`${API}/admin/employees`, authHeader),
        axios.get(`${API}/video-calls/admins`, authHeader),
      ]).then(([empRes, adminRes]) => {
        const emps = empRes.data.employees || empRes.data || [];
        const admins = (adminRes.data.admins || []).filter(a => a.id !== user.id).map(a => ({ ...a, role: "admin" }));
        setPeople([...admins, ...emps]);
      }).catch(() => {});
    } else {
      axios.get(`${API}/video-calls/admins`, authHeader)
        .then(res => setPeople(res.data.admins || []))
        .catch(() => {});
    }
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData, isAdmin]);

  // ─── Actions ───
  const { joinCall: ctxJoinCall } = useCall();

  const callPeople = async () => {
    if (selectedPeople.length === 0) { toast.error("Select someone to call"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/video-calls/invite-to-call`, {
        invitee_ids: selectedPeople,
        message: "",
        enable_recording: false,
      }, authHeader);
      const names = res.data.invitee_names || [];
      toast.success(`Calling ${names.join(", ")}...`);
      setActiveFlow(null);
      setSelectedPeople([]);

      // Pre-populate invitee names so FloatingCall shows "Waiting for..." immediately
      const roomName = res.data.room_name;
      const dailyUrl = res.data.daily_url;
      ctxJoinCall(roomName, dailyUrl, null, names);

      navigate(`/call/${roomName}`);
    } catch {
      toast.error("Failed to start call");
    } finally {
      setCreating(false);
    }
  };

  const workerCallAdmin = async (adminId) => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/video-calls/call-request`, {
        admin_id: adminId,
        message: "",
      }, authHeader);
      toast.success("Calling... waiting for them to answer.");
      navigate(`/call/${res.data.room_name}`);
    } catch {
      toast.error("Failed to start call");
    } finally {
      setCreating(false);
    }
  };

  const createLink = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/video-calls/rooms`, {
        purpose: "ad-hoc",
        expires_minutes: 120,
        enable_recording: false,
      }, authHeader);
      const url = res.data.url;
      const rn = res.data.room_name || res.data.name || "";
      await navigator.clipboard.writeText(`Join me for a video call:\n${url}`);
      setLinkUrl(url);
      setLinkRoomName(rn);
      setLinkCopied(true);
      setActiveFlow("link");
      setEmailTo("");
      setEmailMsg("");
      toast.success("Link copied to clipboard!");
      fetchData();
    } catch {
      toast.error("Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const sendInviteEmail = async () => {
    if (!emailTo || !linkUrl) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/video-calls/send-invite-email`, {
        email: emailTo,
        call_url: linkUrl,
        message: emailMsg,
      }, authHeader);
      toast.success(`Invite sent to ${emailTo}`);
      setEmailTo("");
      setEmailMsg("");
    } catch {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const copyLink = () => {
    if (!linkUrl) return;
    navigator.clipboard.writeText(`Join me for a video call:\n${linkUrl}`);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const scheduleCall = async () => {
    setCreating(true);
    try {
      const scheduledIso = (scheduledDate && scheduledTime)
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : scheduledTime
          ? new Date(`${new Date().toISOString().split("T")[0]}T${scheduledTime}`).toISOString()
          : null;

      if (selectedPeople.length > 0) {
        const res = await axios.post(`${API}/video-calls/invite-to-call`, {
          invitee_ids: selectedPeople,
          message: "",
          enable_recording: false,
          scheduled_at: scheduledIso,
        }, authHeader);
        const url = res.data.daily_url;
        const d = new Date(scheduledIso);
        const timeStr = d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        await navigator.clipboard.writeText(`Join me for a video call at ${timeStr}\n${url}`);
        toast.success("Call scheduled! Invite copied.");
      } else {
        const res = await axios.post(`${API}/video-calls/rooms`, {
          purpose: "ad-hoc",
          expires_minutes: 120,
          scheduled_at: scheduledIso,
        }, authHeader);
        const url = res.data.url;
        const d = new Date(scheduledIso);
        const timeStr = d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        await navigator.clipboard.writeText(`Join me for a video call at ${timeStr}\n${url}`);
        toast.success("Call scheduled! Link copied.");
      }
      setActiveFlow(null);
      setSelectedPeople([]);
      setScheduledDate("");
      setScheduledTime("");
      fetchData();
    } catch {
      toast.error("Failed to schedule call");
    } finally {
      setCreating(false);
    }
  };

  const acceptRequest = async (req) => {
    try {
      const res = await axios.post(`${API}/video-calls/call-requests/${req.id}/accept`, {}, authHeader);
      toast.success(`Joining call with ${req.caller_name}...`);
      navigate(`/call/${res.data.room_name}`);
    } catch {
      toast.error("Failed to accept call");
    }
  };

  const declineRequest = async (req) => {
    try {
      await axios.post(`${API}/video-calls/call-requests/${req.id}/decline`, {}, authHeader);
      toast.info("Call declined");
      fetchData();
    } catch {
      toast.error("Failed to decline");
    }
  };

  const deleteCall = async (roomName) => {
    try {
      await axios.delete(`${API}/video-calls/calls/${roomName}`, authHeader);
      toast.success("Call deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyCallLink = (call) => {
    const url = call.daily_url || `https://thrifty-curator.daily.co/${call.room_name}`;
    navigator.clipboard.writeText(`Join me for a video call:\n${url}`);
    toast.success("Link copied!");
  };

  const activeCalls = calls.filter(c => c.status === "active" || c.status === "pending");
  const scheduledCalls = calls.filter(c => c.status === "scheduled");
  const pastCalls = calls.filter(c => c.status === "ended");

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%)" }} data-testid="video-calls-page">
      {/* Header */}
      <header
        className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/10"
        style={{ background: "linear-gradient(90deg, #1A1A2E, #16213E)", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white hover:bg-white/10 p-2"
            data-testid="video-calls-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-[#00D4FF]" />
            <h1 className="text-lg font-semibold text-white">Video Calls</h1>
          </div>
        </div>
      </header>

      <div className="p-4 max-w-3xl mx-auto space-y-4">
        {/* ─── Quick Actions (Admin) — always at top ─── */}
        {isAdmin && !activeFlow && (
          <div className="grid grid-cols-3 gap-3" data-testid="quick-actions">
            <button
              onClick={() => { setActiveFlow("call"); setSelectedPeople([]); }}
              className="flex flex-col items-center gap-2.5 bg-white/5 hover:bg-[#00D4FF]/10 border border-white/10 hover:border-[#00D4FF]/30 rounded-2xl p-5 transition-all"
              data-testid="action-call-someone"
            >
              <div className="w-12 h-12 rounded-full bg-[#00D4FF]/15 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#00D4FF]" />
              </div>
              <span className="text-white text-sm font-medium">Call</span>
            </button>
            <button
              onClick={createLink}
              disabled={creating}
              className="flex flex-col items-center gap-2.5 bg-white/5 hover:bg-purple-500/10 border border-white/10 hover:border-purple-500/30 rounded-2xl p-5 transition-all"
              data-testid="action-create-link"
            >
              <div className="w-12 h-12 rounded-full bg-purple-500/15 flex items-center justify-center">
                {creating ? <Loader2 className="w-5 h-5 text-purple-400 animate-spin" /> : <Link2 className="w-5 h-5 text-purple-400" />}
              </div>
              <span className="text-white text-sm font-medium">Link</span>
            </button>
            <button
              onClick={() => { setActiveFlow("schedule"); setSelectedPeople([]); setScheduledDate(""); setScheduledTime(""); }}
              className="flex flex-col items-center gap-2.5 bg-white/5 hover:bg-amber-500/10 border border-white/10 hover:border-amber-500/30 rounded-2xl p-5 transition-all"
              data-testid="action-schedule"
            >
              <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-white text-sm font-medium">Schedule</span>
            </button>
          </div>
        )}

        {/* ─── Quick Actions (Employee) — always at top ─── */}
        {!isAdmin && !activeFlow && (
          <div data-testid="worker-call-section">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider px-1 mb-2">Select who to call</p>
            <div className="space-y-1.5">
              {people.map(p => (
                <button
                  key={p.id}
                  onClick={() => togglePerson(p.id)}
                  disabled={creating}
                  className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-all ${
                    selectedPeople.includes(p.id)
                      ? "bg-[#00D4FF]/15 border border-[#00D4FF]/40"
                      : "bg-white/5 border border-transparent hover:bg-white/10"
                  }`}
                  data-testid={`call-person-${p.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedPeople.includes(p.id) ? "bg-[#00D4FF]/30" : "bg-white/10"
                  }`}>
                    {selectedPeople.includes(p.id) ? (
                      <Check className="w-5 h-5 text-[#00D4FF]" />
                    ) : (
                      <Phone className="w-4 h-4 text-white/40" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${selectedPeople.includes(p.id) ? "text-[#00D4FF]" : "text-white"}`}>{p.name}</span>
                </button>
              ))}
            </div>
            {selectedPeople.length > 0 && (
              <Button
                className="w-full bg-[#00D4FF] hover:bg-[#00B8E0] text-black font-semibold h-12 text-sm rounded-2xl mt-3"
                onClick={callPeople}
                disabled={creating}
                data-testid="call-selected-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                Call {selectedPeople.length === 1 ? people.find(p => p.id === selectedPeople[0])?.name : `${selectedPeople.length} people`}
              </Button>
            )}
          </div>
        )}

        {/* ─── Call Flow ─── */}
        {activeFlow === "call" && (
          <PersonPicker
            people={people}
            selected={selectedPeople}
            onToggle={togglePerson}
            onClose={() => setActiveFlow(null)}
            onAction={callPeople}
            actionLabel={selectedPeople.length > 0 ? `Call ${selectedPeople.length} ${selectedPeople.length === 1 ? "person" : "people"}` : "Select someone"}
            actionDisabled={selectedPeople.length === 0}
            loading={creating}
          />
        )}

        {/* ─── Schedule Flow ─── */}
        {activeFlow === "schedule" && (
          <ScheduleForm
            people={people}
            selected={selectedPeople}
            onToggle={togglePerson}
            scheduledDate={scheduledDate}
            setScheduledDate={setScheduledDate}
            scheduledTime={scheduledTime}
            setScheduledTime={setScheduledTime}
            onSchedule={scheduleCall}
            onClose={() => setActiveFlow(null)}
            loading={creating}
          />
        )}

        {/* ─── Link Flow (with email option) ─── */}
        {activeFlow === "link" && linkUrl && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="link-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium text-sm">Call link created</h3>
              <button onClick={() => { setActiveFlow(null); setLinkUrl(null); setLinkRoomName(null); }} className="text-white/30 hover:text-white text-xs">Done</button>
            </div>

            {/* Link display + copy + join */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white/60 text-xs truncate">
                {linkUrl}
              </div>
              <Button
                size="sm"
                onClick={copyLink}
                className={`flex-shrink-0 h-10 px-3 rounded-xl text-xs font-medium ${linkCopied ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-white/10 hover:bg-white/20 text-white"}`}
                data-testid="copy-link-btn"
              >
                {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            {/* Join your own call */}
            {linkRoomName && (
              <Button
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold text-sm h-11 rounded-xl"
                onClick={() => navigate(`/call/${linkRoomName}`)}
                data-testid="join-own-call-btn"
              >
                <Video className="w-4 h-4 mr-2" />
                Join Call
              </Button>
            )}

            {/* Email invite */}
            <div className="border-t border-white/10 pt-3 space-y-2">
              <p className="text-white/40 text-xs font-medium">Or send via email</p>
              <input
                type="email"
                placeholder="Email address"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                className="w-full bg-white/10 border border-white/20 text-white text-sm rounded-xl px-3 py-2.5 placeholder:text-white/25 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]/50"
                data-testid="invite-email-input"
              />
              <input
                type="text"
                placeholder="Add a message (optional)"
                value={emailMsg}
                onChange={e => setEmailMsg(e.target.value)}
                className="w-full bg-white/10 border border-white/20 text-white text-sm rounded-xl px-3 py-2.5 placeholder:text-white/25 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]/50"
                data-testid="invite-email-message"
              />
              <Button
                className="w-full bg-[#00D4FF] hover:bg-[#00B8E0] text-black font-semibold text-sm h-11"
                onClick={sendInviteEmail}
                disabled={!emailTo || sendingEmail}
                data-testid="send-invite-email-btn"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Invite
              </Button>
            </div>
          </div>
        )}

        {/* ─── Pending Incoming Calls ─── */}
        {pendingRequests.length > 0 && (
          <div className="space-y-2" data-testid="pending-requests">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider px-1">Incoming</p>
            {pendingRequests.map(req => (
              <IncomingCallCard
                key={req.id}
                req={req}
                onAccept={() => acceptRequest(req)}
                onDecline={() => declineRequest(req)}
              />
            ))}
          </div>
        )}

        {/* ─── Active Calls ─── */}
        {activeCalls.length > 0 && (
          <div className="space-y-2" data-testid="active-calls">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider px-1">Active</p>
            {activeCalls.map(call => (
              <ActiveCallCard
                key={call.id || call.room_name}
                call={call}
                userName={user.name}
                onJoin={() => navigate(`/call/${call.room_name}`)}
                onCopyLink={() => copyCallLink(call)}
                onDelete={() => deleteCall(call.room_name)}
              />
            ))}
          </div>
        )}

        {/* ─── Scheduled Calls ─── */}
        {scheduledCalls.length > 0 && (
          <div className="space-y-2" data-testid="scheduled-calls">
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider px-1">Scheduled</p>
            {scheduledCalls.map(call => (
              <div
                key={call.id || call.room_name}
                className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 flex items-center justify-between"
                data-testid={`scheduled-call-${call.room_name}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm truncate">{getCallLabel(call, user.name)}</p>
                    <p className="text-amber-300/50 text-xs">{formatDate(call.scheduled_at || call.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black text-xs px-4 h-9 rounded-xl font-semibold"
                    onClick={() => navigate(`/call/${call.room_name}`)}
                    data-testid={`join-scheduled-${call.room_name}`}
                  >
                    Join
                  </Button>
                  <button
                    onClick={() => copyCallLink(call)}
                    className="text-white/25 hover:text-amber-400 p-2 rounded-lg hover:bg-white/10"
                    title="Copy link"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteCall(call.room_name)}
                    className="text-white/15 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── History Section ─── */}
        {(pastCalls.length > 0 || recordings.length > 0) && (
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between py-2 px-1"
              data-testid="toggle-history-btn"
            >
              <span className="text-white/40 text-xs font-medium uppercase tracking-wider">
                History {pastCalls.length > 0 && `(${pastCalls.length})`}
              </span>
              <svg className={`w-4 h-4 text-white/30 transition-transform ${showHistory ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>

            {showHistory && (
              <div className="space-y-2 mt-1">
                {pastCalls.map(call => (
                  <div key={call.id || call.room_name} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-3 flex items-center justify-between" data-testid={`history-call-${call.room_name}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-3.5 h-3.5 text-white/20" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white/60 text-sm truncate">{getCallLabel(call, user.name)}</p>
                        <p className="text-white/25 text-xs">{formatDate(call.created_at)} · {formatDuration(call.duration_seconds)}</p>
                      </div>
                    </div>
                    <button onClick={() => deleteCall(call.room_name)} className="text-white/15 hover:text-red-400 p-2" data-testid={`delete-call-${call.room_name}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {isAdmin && recordings.length > 0 && (
                  <Button
                    variant="ghost"
                    className="w-full text-white/30 hover:text-white/50 text-xs mt-2"
                    onClick={() => navigate("/recordings")}
                    data-testid="view-recordings-btn"
                  >
                    <Circle className="w-3 h-3 text-red-400 fill-red-400 mr-1.5" />
                    {recordings.length} Recording{recordings.length !== 1 ? "s" : ""} Available
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!loading && activeCalls.length === 0 && scheduledCalls.length === 0 && pastCalls.length === 0 && pendingRequests.length === 0 && !activeFlow && (
          <div className="text-center py-8" data-testid="empty-state">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
              <Video className="w-8 h-8 text-white/15" />
            </div>
            <p className="text-white/30 text-sm">No calls yet</p>
            <p className="text-white/15 text-xs mt-1">
              {isAdmin ? "Use the buttons above to start a call" : "Tap a name above to call"}
            </p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#00D4FF] animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
